import type { WatchHandle } from 'vue';
import type { SpeedMonitoringSettings } from '../common/useSystemConfig';
import { judgeSpeedPosition } from '~/composables/geolocations/judgements/speedJudgement';
import { GEOLOCATION_SUBSCRIBER_FEATURES } from '~/stores/geolocations/geolocationStore';

/**
 * 速度監視機能を提供するコンポジション関数。
 *
 * 起動停止と速度判定処理を担う。位置情報の取得・保持はuseGeolocations + storeが担う。
 * 判定結果はstoreに書き戻し、コンポーネントはstoreを参照する。
 *
 * 速度判定はsettings.interval ms周期のsetIntervalで実施。
 * 停車中（同じ位置を継続観測する場合）は速度0を履歴に積み、平均速度を減衰させる。
 */
export function useSpeedMonitor() {
  const logger = useLogger('0-00');
  const { getSpeedMonitoringSettings } = useSystemConfig('0-00');
  const { requestWatch, releaseWatch } = useGeoLocations();
  const store = useGeolocationStore();

  /** 速度監視のシステム設定 */
  let settings: SpeedMonitoringSettings | undefined = undefined;

  /** 速度判定を定期実行する interval の識別子 */
  let intervalId: number | null = null;

  /** 連続でGPSエラーが発生した回数 */
  let consecutiveGpsErrorCount = 0;

  /** 速度超過方向（速度が上限閾値超）の連続判定回数 */
  let consecutiveExceededCount = 0;
  /** 停止方向（速度が下限閾値未満）の連続判定回数 */
  let consecutiveStopCount = 0;

  /** 速度算出に直前使用した位置情報 */
  let prevSpeedSamplePosition: GeolocationPosition | null = null;
  /** 警告抑制状態を監視して自動解除タイマーを管理するwatchハンドル */
  let suppressionWatchHandle: WatchHandle | null = null;
  /** 警告抑制の自動解除タイマー */
  let suppressionTimerId: number | null = null;
  /** startWatchStateの多重watch登録防止用 */
  let startWatchStateHandler: WatchHandle | null = null;

  /**
   * 速度判定処理。
   * store.currentPosition を読んで速度算出・閾値判定を行い、結果をstoreに書き戻す。
   */
  const judgeSpeed = () => {
    if (!settings) {
      return;
    }

    // storeとローカル状態を判定関数の入力に変換する。
    const result = judgeSpeedPosition({
      position: store.currentPosition,
      acquisitionError: store.acquisitionError,
      previousSamplePosition: prevSpeedSamplePosition,
      consecutiveGpsErrorCount,
      isSpeedExceeded: store.isSpeedExceeded,
      consecutiveExceededCount,
      consecutiveStopCount,
      isGpsError: store.isGpsError,
      gpsErrorType: store.gpsErrorType,
      settings,
    });

    // 判定関数が返した次回用のローカル状態をcomposable側で保持する。
    consecutiveGpsErrorCount = result.nextConsecutiveGpsErrorCount;
    consecutiveExceededCount = result.nextConsecutiveExceededCount;
    consecutiveStopCount = result.nextConsecutiveStopCount;
    prevSpeedSamplePosition = result.nextPreviousSamplePosition;

    // 判定関数が返したstore更新値だけを反映する。waiting時は更新しない。
    if (result.speedFields && Object.keys(result.speedFields).length > 0) {
      store.setSpeedFields(result.speedFields);
    }

    // 判定関数はloggerに依存しないため、結果に応じたログ出力はcomposable側で行う。
    if (result.status === 'accuracy-skipped') {
      logger.warn(`位置情報の精度が低いためスキップ: ${result.skippedAccuracy}m`);
      return;
    }

    if (result.status === 'judged') {
      logger.info(`現在の速度: ${result.currentSpeed}m/s`);
    }
  };

  /**
   * 警告抑制状態の自動解除タイマーをセットアップする。
   * isWarningSuppressed が true になったら settings の duration 後に自動的に false に戻す。
   */
  const setupSuppressionAutoClear = () => {
    if (suppressionWatchHandle !== null) {
      return;
    }
    suppressionWatchHandle = watch(() => store.isWarningSuppressed, (isSuppressed) => {
      if (suppressionTimerId !== null) {
        window.clearTimeout(suppressionTimerId);
        suppressionTimerId = null;
      }
      if (isSuppressed) {
        const duration = store.warningSuppressDurationMs ?? 60000;
        suppressionTimerId = window.setTimeout(() => {
          store.setWarningSuppressed(false);
          suppressionTimerId = null;
        }, duration);
      }
    });
  };

  /** 速度監視を開始する */
  const startMonitoring = async () => {
    if (intervalId !== null) {
      return;
    }
    settings = await getSpeedMonitoringSettings();
    store.setWarningSuppressDuration(settings.warningSuppressDurationSeconds * 1000);
    requestWatch(GEOLOCATION_SUBSCRIBER_FEATURES.SPEED_MONITORING);

    consecutiveGpsErrorCount = 0;
    consecutiveExceededCount = 0;
    consecutiveStopCount = 0;
    prevSpeedSamplePosition = null;

    setupSuppressionAutoClear();

    logger.info('速度監視を開始しました');
    intervalId = window.setInterval(judgeSpeed, settings.interval);
  };

  /** 速度監視を停止する */
  const stopMonitoring = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    if (suppressionWatchHandle !== null) {
      suppressionWatchHandle();
      suppressionWatchHandle = null;
    }
    if (suppressionTimerId !== null) {
      window.clearTimeout(suppressionTimerId);
      suppressionTimerId = null;
    }
    consecutiveGpsErrorCount = 0;
    consecutiveExceededCount = 0;
    consecutiveStopCount = 0;
    prevSpeedSamplePosition = null;
    store.resetSpeedStatus();
    releaseWatch(GEOLOCATION_SUBSCRIBER_FEATURES.SPEED_MONITORING);
    logger.info('速度監視を停止しました');
  };

  const { canRunSpeedMonitoring } = useSpeedMonitoringConditions();

  /**
   * 状態を監視し、速度監視の開始と停止を制御する。
   *
   * canRunSpeedMonitoring が true の間に限り速度監視を起動する。
   * （PC端末でない・設定で無効化されていない・ログイン中）
   */
  const startWatchState = () => {
    if (startWatchStateHandler !== null) {
      return;
    }
    let isProcessing: Promise<void> | null = null;
    startWatchStateHandler = watch(canRunSpeedMonitoring, async (canRun) => {
      if (isProcessing) {
        await isProcessing;
      }
      if (canRun) {
        isProcessing = startMonitoring();
      }
      else {
        stopMonitoring();
      }
    }, {
      immediate: true,
    });
  };

  return {
    startWatchState,
  };
}
